/**
 * El split Enterprise vs Business, **explicado y no solo graficado**.
 *
 * El bloque de lectura es el punto: una barra apilada dice que hay
 * concentración, no dice si es un riesgo o una decisión. La lectura arma la frase
 * con las cifras del payload (peso del MRR, peso de los clientes, ticket de cada
 * segmento y su múltiplo), así que no se puede desactualizar respecto de la tabla
 * que tiene al lado.
 *
 * También cuadra el split contra la serie mensual: si los segmentos no suman el
 * MRR del mes, hay plata sin asignar y eso se dice antes de cualquier lectura.
 * Un panel que muestra dos totales distintos del mismo número sin avisar es cómo
 * el sheet original perdió credibilidad.
 */
import {
  SEGMENT_LABELS,
  formatMetricValue,
  formatPeriodo,
  type RevenueMonth,
  type SegmentRevenue,
} from '@ops/contracts'

import {
  Card,
  DataTable,
  EmptyMetricState,
  Icon,
  Panel,
  ProvenanceBadge,
  ShareBar,
  Stack,
  type Column,
  type ShareSegment,
} from '../../ui'
import { segmentosDeUnEje } from '../comunes'

export interface SplitPorSegmentoProps {
  porSegmento: readonly SegmentRevenue[]
  /** Mes del período, para cuadrar la suma de los segmentos contra la serie. */
  mesActual: RevenueMonth | undefined
  periodo: string
  now: Date
}

/** Enterprise es el foco (verde); el resto va en gris: gris no dice "está mal". */
function severidadDe(index: number): ShareSegment['severidad'] {
  return index === 0 ? 'ok' : 'unknown'
}

/** MRR de los segmentos con dato. Es el `total` de la barra y de la leyenda. */
export function totalMrrDe(porSegmento: readonly SegmentRevenue[]): number {
  return porSegmento.reduce((acc, s) => acc + (s.mrr ?? 0), 0)
}

/**
 * Los segmentos de la barra. Exportado para poder probar el invariante de un
 * solo eje sobre el call site real y no solo sobre el helper.
 */
export function segmentosDeMrr(
  porSegmento: readonly SegmentRevenue[],
): ShareSegment[] {
  return segmentosDeUnEje(
    porSegmento.map((s, i) => ({
      id: s.segmento,
      label: SEGMENT_LABELS[s.segmento],
      cantidad: s.mrr === null ? null : Math.round(s.mrr),
      severidad: severidadDe(i),
    })),
    Math.round(totalMrrDe(porSegmento)),
  )
}

export function SplitPorSegmento({
  porSegmento,
  mesActual,
  periodo,
  now,
}: SplitPorSegmentoProps) {
  const conMrr = porSegmento.filter((s) => s.mrr !== null)
  const totalMrr = totalMrrDe(porSegmento)
  const totalClientes = porSegmento.reduce((acc, s) => acc + s.clientes, 0)
  const mrrDelMes = mesActual?.mrr ?? null
  const descuadre =
    mrrDelMes === null || conMrr.length === 0
      ? null
      : Math.round(mrrDelMes - totalMrr)

  /*
   * La barra es de **MRR**, un solo eje: `cantidad` es el importe del segmento y
   * `total` el MRR de los segmentos con dato, así que el porcentaje de la
   * leyenda se puede recalcular con los dos números que están al lado.
   *
   * Antes pegaba la cantidad de clientes al porcentaje del MRR y la leyenda
   * decía "Enterprise 12 (76,1%)": 12 sobre 31 cuentas es 38,7%, no 76,1%. Dos
   * magnitudes distintas presentadas como una, y encima el ancho de la barra era
   * el del MRR mientras el número impreso adentro era el de clientes. La cuenta
   * de clientes no se perdió: está en la columna "Clientes" de la tabla de
   * abajo, en la lectura y en el subtítulo de este mismo panel.
   *
   * Aquel arreglo cambió el campo pero no la forma de armarlo: `pct` seguía
   * viniendo de `s.pctMrr`, un campo del payload independiente del que produce
   * `cantidad`, así que los dos podían volver a divergir. `segmentosDeUnEje`
   * **deriva** el porcentaje del mismo importe y del mismo total que la barra
   * publica; `s.pctMrr` queda para la columna "% del revenue" de la tabla, que
   * es el número del servidor con su propio total.
   */
  const segmentos: ShareSegment[] = segmentosDeMrr(porSegmento)

  const columnas: Column<SegmentRevenue>[] = [
    {
      key: 'segmento',
      header: 'Segmento',
      rowHeader: true,
      sortValue: (s) => SEGMENT_LABELS[s.segmento],
      render: (s) => SEGMENT_LABELS[s.segmento],
    },
    {
      key: 'mrr',
      header: 'MRR',
      align: 'right',
      sortValue: (s) => s.mrr,
      render: (s) =>
        s.mrr === null ? (
          <EmptyMetricState
            metricNombre={`MRR de ${SEGMENT_LABELS[s.segmento]}`}
            detalle="El segmento no tiene clientes con MRR cargado."
            size="sm"
          />
        ) : (
          formatMetricValue(s.mrr, 'usd')
        ),
    },
    {
      key: 'pctMrr',
      header: '% del revenue',
      align: 'right',
      sortValue: (s) => s.pctMrr,
      render: (s) =>
        s.pctMrr === null ? (
          <span className="text-micro text-muted-strong">sin MRR total</span>
        ) : (
          formatMetricValue(s.pctMrr, 'percent')
        ),
    },
    {
      key: 'clientes',
      header: 'Clientes',
      align: 'right',
      sortValue: (s) => s.clientes,
      render: (s) => formatMetricValue(s.clientes, 'count'),
    },
    {
      key: 'arpu',
      header: 'Ticket promedio',
      align: 'right',
      sortValue: (s) => s.arpu,
      render: (s) =>
        s.arpu === null ? (
          <span className="text-micro text-muted-strong">sin clientes</span>
        ) : (
          formatMetricValue(s.arpu, 'usd')
        ),
    },
    {
      key: 'provenance',
      header: 'Proveniencia',
      render: (s) => (
        <ProvenanceBadge provenance={s.provenance} now={now} hrefAdmin="/admin" />
      ),
    },
  ]

  return (
    <Stack gap="md">
      {descuadre !== null && descuadre !== 0 ? (
        <Card severidad="warning" padding="sm">
          <p
            role="status"
            className="inline-flex items-start gap-1.5 text-meta text-text"
          >
            <Icon name="atencion" size={13} className="mt-px text-warning-text" />
            <span>
              El split no cuadra con la serie mensual: los segmentos suman{' '}
              <b className="font-semibold">{formatMetricValue(totalMrr, 'usd')}</b>{' '}
              y {formatPeriodo(periodo)} dice{' '}
              <b className="font-semibold">
                {formatMetricValue(mrrDelMes, 'usd')}
              </b>
              . Hay{' '}
              <b className="font-semibold">
                {formatMetricValue(Math.abs(descuadre), 'usd')}
              </b>{' '}
              sin asignar a ningún segmento: o falta segmentar clientes, o la fila
              del mes está mal.
            </span>
          </p>
        </Card>
      ) : null}

      <Panel
        titulo="Dónde está el MRR"
        subtitulo={`Reparto de los ${formatMetricValue(Math.round(totalMrr), 'usd')} de MRR de ${formatPeriodo(periodo)}. La cuenta de clientes es otro eje y va aparte: ${formatMetricValue(totalClientes, 'count')} cuentas, por segmento en la tabla de abajo.`}
      >
        <Stack gap="md">
          <ShareBar
            segmentos={segmentos}
            label={`MRR de ${formatPeriodo(periodo)} por segmento`}
            total={Math.round(totalMrr)}
            unidad="usd"
          />
          <Lectura porSegmento={porSegmento} totalClientes={totalClientes} />
        </Stack>
      </Panel>

      <DataTable
        columns={columnas}
        rows={porSegmento}
        rowKey={(s) => s.segmento}
        density="normal"
        emptyMessage="Todavía no hay clientes segmentados."
        caption={
          <>
            MRR, clientes y ticket promedio por segmento. El MRR sale de la suma
            de los clientes del portfolio, así que el reparto siempre cierra con
            la tabla de Clientes.
          </>
        }
        footer={
          <span className="tabular">
            Total: {formatMetricValue(totalMrr, 'usd')} ·{' '}
            {formatMetricValue(totalClientes, 'count')} clientes · ticket
            promedio{' '}
            {totalClientes === 0
              ? 'sin clientes'
              : formatMetricValue(
                  Math.round(totalMrr / totalClientes),
                  'usd',
                )}
          </span>
        }
      />
    </Stack>
  )
}

/**
 * La lectura. Sin esto el bloque son dos números que cada persona interpreta como
 * quiere; con esto la pantalla dice qué está mirando y con qué cifras lo dice.
 */
function Lectura({
  porSegmento,
  totalClientes,
}: {
  porSegmento: readonly SegmentRevenue[]
  totalClientes: number
}) {
  const foco = porSegmento[0]
  const cola = porSegmento[1]

  if (
    foco === undefined ||
    cola === undefined ||
    foco.arpu === null ||
    cola.arpu === null ||
    cola.arpu === 0 ||
    foco.pctMrr === null ||
    totalClientes === 0
  ) {
    return (
      <Card padding="sm" hundida punteada>
        <p className="text-meta text-muted-strong">
          Con un solo segmento con datos no hay concentración que leer. La lectura
          aparece cuando los dos segmentos tienen clientes y ticket.
        </p>
      </Card>
    )
  }

  const multiplo = foco.arpu / cola.arpu
  const pctClientesFoco = (foco.clientes / totalClientes) * 100
  const pctClientesCola = (cola.clientes / totalClientes) * 100

  return (
    <Card padding="sm" severidad="ok">
      <Stack gap="xs">
        <h4 className="inline-flex items-center gap-1.5 text-titulo text-text">
          <Icon name="ok" size={14} className="text-accent-text" />
          La concentración es una decisión, no un accidente
        </h4>
        <p className="max-w-prosa text-meta leading-relaxed text-muted-strong [&_b]:font-semibold [&_b]:text-text">
          {SEGMENT_LABELS[foco.segmento]} tiene un ticket de{' '}
          <b>{formatMetricValue(foco.arpu, 'usd')}</b> contra{' '}
          <b>{formatMetricValue(cola.arpu, 'usd')}</b> de{' '}
          {SEGMENT_LABELS[cola.segmento]}:{' '}
          <b>{formatMetricValue(multiplo, 'ratio')}</b> más por cuenta. Por eso{' '}
          <b>{formatMetricValue(foco.pctMrr, 'percent')}</b> del revenue sale de{' '}
          <b>{formatMetricValue(pctClientesFoco, 'percent')}</b> de los clientes,
          y {SEGMENT_LABELS[cola.segmento]} aporta{' '}
          <b>{formatMetricValue(cola.pctMrr ?? 0, 'percent')}</b> con{' '}
          <b>{formatMetricValue(pctClientesCola, 'percent')}</b> de las cuentas.
          El peso de {SEGMENT_LABELS[foco.segmento]} es el resultado de vender al
          segmento de ticket alto, no de haber perdido el otro: mover una cuenta
          de {SEGMENT_LABELS[cola.segmento]} a {SEGMENT_LABELS[foco.segmento]}{' '}
          vale{' '}
          <b>
            {formatMetricValue(
              Math.round(foco.arpu - cola.arpu),
              'usd',
            )}
          </b>{' '}
          de MRR por mes.
        </p>
        <p className="text-micro text-muted-strong">
          Base de la lectura: {SEGMENT_LABELS[foco.segmento]}{' '}
          {formatMetricValue(foco.mrr, 'usd')} /{' '}
          {formatMetricValue(foco.clientes, 'count')} cuentas ·{' '}
          {SEGMENT_LABELS[cola.segmento]} {formatMetricValue(cola.mrr, 'usd')} /{' '}
          {formatMetricValue(cola.clientes, 'count')} cuentas.
        </p>
      </Stack>
    </Card>
  )
}
